import "./Dashboard.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaBoxes,
  FaChartLine,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaShoppingCart,
  FaWallet,
} from "react-icons/fa";
import client from "../api/client";
import DiwaliJhalar from "../components/DiwaliJhalar";

const emptyStats = {
  totalSales: 0,
  totalRevenue: 0,
  totalCustomers: 0,
  totalProducts: 0,
  totalStock: 0,
  totalRto: 0,
  chartData: [],
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("en-IN");

const chartPalette = ["#4f8df7", "#28b487", "#8d6bea", "#f59e0b", "#ef6363", "#14b8a6"];

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatCurrency = (value) => currencyFormatter.format(toNumber(value));
const formatNumber = (value) => numberFormatter.format(toNumber(value));

const toDateTimeLocal = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const getTodayRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return { from: toDateTimeLocal(start), to: toDateTimeLocal(now) };
};

const formatPercent = (value) => {
  if (!Number.isFinite(value)) return "0%";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};

const getGrowth = (current, previous) => {
  const currentValue = toNumber(current);
  const previousValue = toNumber(previous);
  if (!previousValue) return currentValue ? 100 : 0;
  return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
};

const getPendingPayments = (stats) =>
  toNumber(
    stats.pendingPayments ??
      stats.pending_payments ??
      stats.pendingPaymentAmount ??
      stats.pending_payment_amount ??
      stats.pendingAmount ??
      stats.pending_amount
  );

const getOrderStatus = (order) =>
  String(order?.status || order?.order_status || order?.tracking_status || "Pending").trim() || "Pending";

const getRecordDate = (record) =>
  record?.created_at || record?.updated_at || record?.timestamp || record?.date || record?.entry_date || "";

const makeStatusRows = (orders) => {
  const counts = new Map();
  orders.forEach((order) => {
    const status = getOrderStatus(order);
    counts.set(status, (counts.get(status) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([status, count]) => ({ status, count }));
};

const makeInventoryRows = (inventory) => {
  const counts = new Map();
  inventory.forEach((item) => {
    const category = String(item?.category || "Uncategorized").trim() || "Uncategorized";
    counts.set(category, (counts.get(category) || 0) + toNumber(item?.stock));
  });
  return Array.from(counts.entries()).map(([category, stock]) => ({ category, stock }));
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(emptyStats);
  const [inventory, setInventory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activeMetric, setActiveMetric] = useState("revenue");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getTodayRange);

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsResult, inventoryResult, ordersResult, activityResult] = await Promise.allSettled([
          client.get("/reports/dashboard-stats", { params: dateRange }),
          client.get("/inventory"),
          client.get("/orders"),
          client.get("/activity", { params: { limit: 8 } }),
        ]);

        if (!isMounted) return;

        if (statsResult.status === "fulfilled") {
          setStats({ ...emptyStats, ...(statsResult.value.data || {}) });
        } else {
          console.error("Error fetching dashboard stats:", statsResult.reason);
          setStats(emptyStats);
        }

        setInventory(
          inventoryResult.status === "fulfilled" && Array.isArray(inventoryResult.value.data)
            ? inventoryResult.value.data
            : []
        );
        setOrders(
          ordersResult.status === "fulfilled" && Array.isArray(ordersResult.value.data)
            ? ordersResult.value.data
            : []
        );
        setActivities(
          activityResult.status === "fulfilled" && Array.isArray(activityResult.value.data?.data)
            ? activityResult.value.data.data
            : []
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [dateRange]);

  const chartData = useMemo(
    () =>
      (Array.isArray(stats.chartData) ? stats.chartData : []).map((row) => {
        const revenue = toNumber(row.revenue);
        const expenses = toNumber(row.expenses ?? row.expense ?? row.totalExpenses ?? row.total_expenses);
        return {
          ...row,
          month: row.month || row.label || "",
          sales: toNumber(row.sales),
          revenue,
          expenses,
          profit: revenue - expenses,
        };
      }),
    [stats.chartData]
  );

  const comparison = useMemo(() => {
    const current = chartData.at(-1) || {};
    const previous = chartData.at(-2) || {};
    const lastYear = chartData.find((row) => row.yearAgo || row.sameMonthLastYear) || {};

    return [
      {
        label: "Current Month",
        revenue: toNumber(current.revenue),
        sales: toNumber(current.sales),
        profit: toNumber(current.profit),
      },
      {
        label: "Previous Month",
        revenue: toNumber(previous.revenue),
        sales: toNumber(previous.sales),
        profit: toNumber(previous.profit),
      },
      {
        label: "Same Month Last Year",
        revenue: toNumber(lastYear.revenue ?? lastYear.sameMonthLastYearRevenue),
        sales: toNumber(lastYear.sales ?? lastYear.sameMonthLastYearSales),
        profit: toNumber(lastYear.profit ?? lastYear.sameMonthLastYearProfit),
      },
    ];
  }, [chartData]);

  const trends = useMemo(() => {
    const current = chartData.at(-1) || {};
    const previous = chartData.at(-2) || {};
    return {
      revenueGrowth: getGrowth(current.revenue, previous.revenue),
      salesGrowth: getGrowth(current.sales, previous.sales),
      profitGrowth: getGrowth(current.profit, previous.profit),
    };
  }, [chartData]);

  const pendingPayments = useMemo(() => getPendingPayments(stats), [stats]);
  const orderRows = useMemo(() => makeStatusRows(orders), [orders]);
  const inventoryRows = useMemo(() => makeInventoryRows(inventory), [inventory]);
  const inventoryTotalUnits = useMemo(
    () => inventoryRows.reduce((total, row) => total + toNumber(row.stock), 0),
    [inventoryRows]
  );
  const inventoryChartRows = useMemo(
    () =>
      inventoryRows.map((row) => ({
        ...row,
        percentage: inventoryTotalUnits ? (toNumber(row.stock) / inventoryTotalUnits) * 100 : 0,
      })),
    [inventoryRows, inventoryTotalUnits]
  );

  const orderCompletionRate = useMemo(() => {
    if (!orders.length) return 0;
    const complete = orders.filter((order) => /delivered|completed|accepted/i.test(getOrderStatus(order))).length;
    return (complete / orders.length) * 100;
  }, [orders]);

  const inventoryHealth = useMemo(() => {
    if (!inventory.length) return 0;
    const inStock = inventory.filter((item) => toNumber(item.stock) > 0).length;
    return (inStock / inventory.length) * 100;
  }, [inventory]);

  const kpiCards = useMemo(
    () => [
      {
        id: "revenue",
        label: "Total Revenue",
        value: formatCurrency(stats.totalRevenue),
        rawValue: toNumber(stats.totalRevenue),
        trend: trends.revenueGrowth,
        icon: <FaMoneyBillWave />,
        className: "revenue",
      },
      {
        id: "sales",
        label: "Total Sales",
        value: formatNumber(stats.totalSales),
        rawValue: toNumber(stats.totalSales),
        trend: trends.salesGrowth,
        icon: <FaShoppingCart />,
        className: "sales",
      },
      {
        id: "products",
        label: "Total Products",
        value: formatNumber(stats.totalProducts),
        rawValue: toNumber(stats.totalProducts),
        trend: inventoryHealth,
        icon: <FaBoxes />,
        className: "products",
      },
      {
        id: "payments",
        label: "Pending Payments",
        value: formatCurrency(pendingPayments),
        rawValue: pendingPayments,
        trend: pendingPayments ? -Math.abs(trends.revenueGrowth) : 0,
        icon: <FaWallet />,
        className: "payments",
      },
    ],
    [inventoryHealth, pendingPayments, stats.totalProducts, stats.totalRevenue, stats.totalSales, trends]
  );

  const activeCard = useMemo(
    () => kpiCards.find((card) => card.id === activeMetric) || kpiCards[0],
    [activeMetric, kpiCards]
  );

  const alerts = useMemo(() => {
    const lowStock = inventory
      .filter((item) => toNumber(item.stock) > 0)
      .sort((a, b) => toNumber(a.stock) - toNumber(b.stock))
      .slice(0, 3)
      .map((item) => ({
        title: item.name || item.product_name || "Inventory item",
        detail: `${formatNumber(item.stock)} units available`,
        type: "Inventory",
      }));

    const delayedOrders = orders
      .filter((order) => /delay|late|pending/i.test(getOrderStatus(order)))
      .slice(0, 3)
      .map((order) => ({
        title: order.customer || order.customer_name || order.product || `Order #${order.id || ""}`.trim(),
        detail: getOrderStatus(order),
        type: "Orders",
      }));

    const manufacturingIssues = orders
      .filter((order) => /manufactur|damage|issue|return|rto|dto/i.test(getOrderStatus(order)))
      .slice(0, 3)
      .map((order) => ({
        title: order.product || order.customer || `Order #${order.id || ""}`.trim(),
        detail: getOrderStatus(order),
        type: "Manufacturing",
      }));

    const paymentAlert =
      pendingPayments > 0
        ? [{ title: "Pending payment exposure", detail: formatCurrency(pendingPayments), type: "Payments" }]
        : [];

    return [...paymentAlert, ...lowStock, ...delayedOrders, ...manufacturingIssues].slice(0, 7);
  }, [inventory, orders, pendingPayments]);

  const recentActivity = useMemo(() => {
    if (activities.length) {
      return activities.slice(0, 8).map((activity) => ({
        title: activity.action || activity.module || "Business activity",
        detail: activity.description || activity.actorName || activity.employeeName || "",
        date: getRecordDate(activity),
      }));
    }

    return chartData
      .slice(-6)
      .reverse()
      .map((row) => ({
        title: `${row.month} sales updated`,
        detail: `${formatNumber(row.sales)} sales, ${formatCurrency(row.revenue)} revenue`,
        date: row.month,
      }));
  }, [activities, chartData]);

  const insights = useMemo(() => {
    const bestMonth = chartData.reduce(
      (best, row) => (toNumber(row.revenue) > toNumber(best.revenue) ? row : best),
      chartData[0] || {}
    );
    const fastestCategory = inventoryRows.reduce(
      (best, row) => (toNumber(row.stock) > toNumber(best.stock) ? row : best),
      inventoryRows[0] || {}
    );

    return [
      {
        label: "Best performing month",
        value: bestMonth.month || "No data",
        note: bestMonth.month ? formatCurrency(bestMonth.revenue) : "Waiting for sales history",
      },
      {
        label: "Fastest growing category",
        value: fastestCategory.category || "No data",
        note: fastestCategory.category ? `${formatNumber(fastestCategory.stock)} units in stock` : "Waiting for inventory data",
      },
      {
        label: "Revenue trend",
        value: formatPercent(trends.revenueGrowth),
        note: "Compared with previous month",
      },
      {
        label: "Pending payment trend",
        value: pendingPayments ? formatCurrency(pendingPayments) : formatCurrency(0),
        note: "From available payment fields",
      },
    ];
  }, [chartData, inventoryRows, pendingPayments, trends.revenueGrowth]);

  if (loading) {
    return (
      
        <div className="dashboard-shell">
          <DiwaliJhalar />
          <div className="dashboard-loading-panel">
            <div className="dashboard-loading-orbit" />
            <h2>Dashboard</h2>
            <p>Loading live business data...</p>
          </div>
        </div>
      
    );
  }

  return (
    
      <div className="dashboard-shell">
        <DiwaliJhalar />
        <header className="dashboard-hero">
          <div>
            <span className="dashboard-eyebrow">Executive Overview</span>
            <h1>Dashboard</h1>
            <p>Live business performance from connected sales, inventory, orders, and activity data.</p>
          </div>
          <div className="dashboard-hero-tools">
            <button type="button" className="dashboard-recent-logs-btn" onClick={() => navigate("/recent-logs")}>Recent Logs</button>
            <div className="dashboard-date-filter">
              <label>
                From
                <input
                  type="datetime-local"
                  value={dateRange.from}
                  max={dateRange.to}
                  onChange={(event) => setDateRange((prev) => ({ ...prev, from: event.target.value }))}
                />
              </label>
              <label>
                To
                <input
                  type="datetime-local"
                  value={dateRange.to}
                  min={dateRange.from}
                  onChange={(event) => setDateRange((prev) => ({ ...prev, to: event.target.value }))}
                />
              </label>
            </div>
            <div className="dashboard-hero-metric">
              <FaChartLine />
              <span>Revenue Growth</span>
              <strong>{formatPercent(trends.revenueGrowth)}</strong>
            </div>
          </div>
        </header>

        <section className="dashboard-kpi-grid" aria-label="Dashboard metrics">
          {kpiCards.map((card) => (
            <button
              type="button"
              key={card.id}
              className={`dashboard-kpi-card ${card.className} ${activeMetric === card.id ? "is-active" : ""}`}
              onClick={() => setActiveMetric(card.id)}
            >
              <span className="kpi-card-top">
                <span className="kpi-icon">{card.icon}</span>
                <span className={`kpi-trend ${card.trend < 0 ? "is-down" : "is-up"}`}>{formatPercent(card.trend)}</span>
              </span>
              <span className="kpi-label">{card.label}</span>
              <strong>{card.value}</strong>
            </button>
          ))}
        </section>

        <section className="analytics-panel">
          <div className="section-heading">
            <div>
              <span className="dashboard-eyebrow">Selected Metric</span>
              <h2>{activeCard.label} Analytics</h2>
            </div>
            <strong>{activeCard.value}</strong>
          </div>

          <div className="analytics-grid">
            <div className="analytics-main-chart">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="activeMetricFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f8df7" stopOpacity={0.32} />
                      <stop offset="95%" stopColor="#4f8df7" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8edf5" />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <Tooltip formatter={(value, name) => (name === "revenue" || name === "profit" ? formatCurrency(value) : formatNumber(value))} />
                  <Area
                    type="monotone"
                    dataKey={activeMetric === "sales" ? "sales" : activeMetric === "products" ? "profit" : "revenue"}
                    stroke="#2463eb"
                    strokeWidth={3}
                    fill="url(#activeMetricFill)"
                    animationDuration={800}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="analytics-breakdown">
              {comparison.map((row) => (
                <div className="comparison-row" key={row.label}>
                  <span>{row.label}</span>
                  <strong>{activeMetric === "sales" ? formatNumber(row.sales) : formatCurrency(row.revenue)}</strong>
                  <small>{formatCurrency(row.profit)} profit</small>
                </div>
              ))}
              <div className="trend-summary">
                <span>Trend Analysis</span>
                <strong>{formatPercent(activeMetric === "sales" ? trends.salesGrowth : trends.revenueGrowth)}</strong>
                <small>Growth percentage is calculated from the two latest chart periods.</small>
              </div>
            </div>
          </div>
        </section>

        <section className="dashboard-chart-grid">
          <article className="dashboard-chart-card chart-wide">
            <div className="chart-title-row">
              <h3>Revenue vs Expenses</h3>
              <span>Monthly comparison</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="revenue" fill="#4f8df7" radius={[0, 0, 0, 0]} animationDuration={800} />
                <Bar dataKey="expenses" fill="#f59e0b" radius={[0, 0, 0, 0]} animationDuration={800} />
              </ComposedChart>
            </ResponsiveContainer>
          </article>

          <article className="dashboard-chart-card">
            <div className="chart-title-row">
              <h3>Monthly Sales Trend</h3>
              <span>Sales volume</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip formatter={(value) => formatNumber(value)} />
                <Line type="monotone" dataKey="sales" stroke="#28b487" strokeWidth={3} dot={{ r: 4 }} animationDuration={800} />
              </LineChart>
            </ResponsiveContainer>
          </article>

          <article className="dashboard-chart-card">
            <div className="chart-title-row">
              <h3>Profit Trend</h3>
              <span>Revenue less available expenses</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Area type="monotone" dataKey="profit" stroke="#8d6bea" fill="#efe9ff" animationDuration={800} />
              </AreaChart>
            </ResponsiveContainer>
          </article>

          <article className="dashboard-chart-card">
            <div className="chart-title-row">
              <h3>Inventory Status</h3>
              <span>{formatNumber(inventoryTotalUnits)} units in stock</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={inventoryChartRows} dataKey="stock" nameKey="category" innerRadius={52} outerRadius={82} animationDuration={800}>
                  {inventoryChartRows.map((entry, index) => (
                    <Cell key={entry.category} fill={chartPalette[index % chartPalette.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, _name, item) => [`${formatNumber(value)} units (${toNumber(item?.payload?.percentage).toFixed(1)}%)`, "Stock"]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="inventory-stock-summary">
              {inventoryChartRows.map((row, index) => (
                <div key={row.category}>
                  <span className="inventory-stock-dot" style={{ background: chartPalette[index % chartPalette.length] }} />
                  <span>{row.category}</span>
                  <strong>{formatNumber(row.stock)} units · {row.percentage.toFixed(1)}%</strong>
                </div>
              ))}
            </div>
          </article>

          <article className="dashboard-chart-card">
            <div className="chart-title-row">
              <h3>Orders Status</h3>
              <span>Status distribution</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={orderRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="status" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip formatter={(value) => formatNumber(value)} />
                <Bar dataKey="count" fill="#14b8a6" radius={[0, 0, 0, 0]} animationDuration={800} />
              </BarChart>
            </ResponsiveContainer>
          </article>
        </section>

        <section className="dashboard-lower-grid">
          <article className="dashboard-panel">
            <div className="section-heading compact">
              <h2>Business Health</h2>
            </div>
            <div className="health-grid">
              <div><span>Revenue Growth</span><strong>{formatPercent(trends.revenueGrowth)}</strong></div>
              <div><span>Profit Growth</span><strong>{formatPercent(trends.profitGrowth)}</strong></div>
              <div><span>Pending Payments</span><strong>{formatCurrency(pendingPayments)}</strong></div>
              <div><span>Inventory Health</span><strong>{formatPercent(inventoryHealth)}</strong></div>
              <div><span>Order Completion</span><strong>{formatPercent(orderCompletionRate)}</strong></div>
            </div>
          </article>

          <article className="dashboard-panel">
            <div className="section-heading compact">
              <h2>Critical Alerts</h2>
              <FaExclamationTriangle />
            </div>
            <div className="alert-list">
              {alerts.length ? (
                alerts.map((alert, index) => (
                  <div className="alert-row" key={`${alert.type}-${alert.title}-${index}`}>
                    <span>{alert.type}</span>
                    <strong>{alert.title}</strong>
                    <small>{alert.detail}</small>
                  </div>
                ))
              ) : (
                <div className="empty-dashboard-state">No active alerts from available data.</div>
              )}
            </div>
          </article>

          <article className="dashboard-panel">
            <div className="section-heading compact">
              <h2>Recent Activity</h2>
            </div>
            <div className="activity-timeline">
              {recentActivity.length ? (
                recentActivity.map((activity, index) => (
                  <div className="timeline-row" key={`${activity.title}-${index}`}>
                    <span />
                    <div>
                      <strong>{activity.title}</strong>
                      <p>{activity.detail}</p>
                      <small>{activity.date}</small>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-dashboard-state">No recent activity available.</div>
              )}
            </div>
          </article>

          <article className="dashboard-panel">
            <div className="section-heading compact">
              <h2>Insights</h2>
            </div>
            <div className="insight-list">
              {insights.map((insight) => (
                <div className="insight-row" key={insight.label}>
                  <span>{insight.label}</span>
                  <strong>{insight.value}</strong>
                  <small>{insight.note}</small>
                </div>
              ))}
            </div>
          </article>
        </section>
      </div>
    
  );
}
